/**
 * useChat — the React binding for the chat feature.
 *
 * Strict layering: pages call THIS hook; the hook calls {@link ChatStore}; the
 * store calls the api facade (documents + inference). Pages never import the
 * store or the api directly.
 *
 * Responsibilities:
 * - Eagerly load the conversation list (summaries) for the sidebar.
 * - Own the ACTIVE conversation: create a new (in-memory) one, select/load an
 *   existing one, or clear it.
 * - Drive a send: append the user turn, persist, call REAL Bedrock
 *   (`store.converse`) with the architecture system prompt, then append the
 *   assistant turn (or an inline error turn) and re-persist.
 * - Own the per-conversation model selection.
 *
 * Persistence is lazy: a brand-new conversation is only written on its first
 * send (create), then updated in place (save) on every subsequent turn — so
 * empty conversations never litter the store.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { ChatStore } from './chatStore';
import {
  ARCHITECTURE_SYSTEM_PROMPT,
  DEFAULT_MODEL_ID,
  applyKbSelection,
  blankConversation,
  buildConversePrompt,
  deriveTitle,
  makeMessage,
  resolveKbSelection,
  sortConversationsByRecency,
} from './chatModel';
import type { Conversation, ConversationSummary } from './types';
import type { TenantKnowledgeBase } from '../../api';

/**
 * Maps a raw inference error into a clear, user-facing inline message. Bedrock
 * "model access not enabled" is the expected pre-provisioning case, so we call
 * it out explicitly; everything else falls back to the raw message.
 */
function friendlyInferenceError(raw: string): string {
  const lower = raw.toLowerCase();
  if (
    lower.includes('access') &&
    (lower.includes('model') || lower.includes('denied') || lower.includes('bedrock'))
  ) {
    return [
      '⚠️ **The assistant is unavailable right now.**',
      '',
      'This usually means Bedrock model access is not yet enabled for this',
      'account, or the configured `modelId` is not provisioned. Once model',
      'access is granted, replies will generate normally — your messages are',
      'saved either way.',
      '',
      `_Details: ${raw}_`,
    ].join('\n');
  }
  return [
    "⚠️ **Couldn't generate a reply.**",
    '',
    `_Details: ${raw}_`,
  ].join('\n');
}

/**
 * KB-grounded variant of the friendly error. Calls out the two expected
 * pre-provisioning cases: knowledge-base access not granted, or the configured
 * generation model/inference-profile not enabled.
 */
function friendlyKbError(raw: string): string {
  return [
    '⚠️ **KB-grounded answer unavailable.**',
    '',
    'This usually means the knowledge base is not accessible to this account',
    'yet, or the generation model / inference profile it resolves to is not',
    'enabled. You can switch to **plain LLM** mode above — your message is',
    'saved either way.',
    '',
    `_Details: ${raw}_`,
  ].join('\n');
}

export interface UseChatResult {
  /** All non-deleted conversation summaries, newest-activity-first. */
  conversations: ConversationSummary[];
  /** The open conversation (may be unsaved), or null when none is open. */
  active: Conversation | null;
  /** The effective model id for the open/next conversation. */
  modelId: string;
  /** True while the conversation list is (re)loading. */
  loading: boolean;
  /** True while the active conversation document is loading. */
  loadingActive: boolean;
  /** True while a Bedrock reply is being generated. */
  sending: boolean;
  /** List-level load error, if any. */
  error: string | null;
  /** Re-fetches the conversation list. */
  refresh: () => Promise<void>;
  /** Clears the active conversation to start a fresh one. */
  newConversation: () => void;
  /** Loads an existing conversation into the active slot. */
  select: (id: string) => Promise<void>;
  /** Sends a user message and generates the assistant reply. */
  send: (text: string) => Promise<void>;
  /** Changes the model for the active (or next) conversation. */
  setModelId: (modelId: string) => void;
  /** Soft-deletes a conversation. */
  remove: (id: string) => Promise<void>;
  /** True when replies are KB-grounded (RetrieveAndGenerate); false = plain LLM. */
  grounded: boolean;
  /** Toggles KB-grounded vs plain-LLM generation. */
  setGrounded: (grounded: boolean) => void;
  /** The tenant's registered knowledge bases (drives the KB picker). */
  tenantKbs: TenantKnowledgeBase[];
  /**
   * The effective selected KB bundle key for the open/next conversation, or
   * null when the tenant has no registered KBs (grounding falls back to plain).
   */
  selectedKbKey: string | null;
  /**
   * True when a KB-grounded reply will actually be produced for the next send:
   * the grounding toggle is on AND a KB is selectable. When false, sends use
   * the plain-LLM path (including the no-registered-KBs fallback).
   */
  kbActive: boolean;
  /** Selects the KB the active/next conversation's grounded turns are routed to. */
  setKbKey: (bundleKey: string) => void;
  /** True while an upload+ingest job is in flight. */
  ingesting: boolean;
  /** The latest ingestion status line (or null). */
  ingestStatus: string | null;
  /** Uploads a text document to the KB bucket and starts ingestion. */
  ingestFile: (fileName: string, content: string) => Promise<void>;
}

export function useChat(): UseChatResult {
  const api = useApi();
  const store = useMemo(() => new ChatStore(api), [api]);

  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [active, setActive] = useState<Conversation | null>(null);
  const [draftModelId, setDraftModelId] = useState<string>(DEFAULT_MODEL_ID);
  const [loading, setLoading] = useState(true);
  const [loadingActive, setLoadingActive] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // KB grounding + ingestion state. When the tenant has registered knowledge
  // bases, a picker selects WHICH one grounds the reply (routed by bundle key);
  // with none registered, chat falls back to the plain-LLM path.
  const [grounded, setGrounded] = useState(true);
  const [tenantKbs, setTenantKbs] = useState<TenantKnowledgeBase[]>([]);
  const [draftKbKey, setDraftKbKey] = useState<string | null>(null);
  const [ingesting, setIngesting] = useState(false);
  const [ingestStatus, setIngestStatus] = useState<string | null>(null);

  /** Ids known to be persisted (loaded or created) → decides create vs save. */
  const persistedIds = useRef<Set<string>>(new Set());
  /** Guards against out-of-order conversation loads. */
  const loadSeq = useRef(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await store.listSummaries();
      setConversations(sortConversationsByRecency(rows));
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Failed to load conversations.',
      );
    } finally {
      setLoading(false);
    }
  }, [store]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Load the tenant's registered knowledge bases once (drives the picker).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const kbs = await store.listTenantKnowledgeBases();
        if (!cancelled) setTenantKbs(kbs);
      } catch {
        if (!cancelled) setTenantKbs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [store]);

  // The effective picker selection: the active conversation's remembered KB,
  // else the last draft, else the first available (null when none registered).
  const selectedKbKey = useMemo(
    () => resolveKbSelection(active?.kbBundleKey ?? null, draftKbKey, tenantKbs),
    [active?.kbBundleKey, draftKbKey, tenantKbs],
  );

  // A grounded reply is only produced when grounding is on AND a KB is
  // selectable; otherwise (toggle off, or no registered KBs) we use plain LLM.
  const kbActive = grounded && selectedKbKey !== null;

  /** create-once, then save-in-place. */
  const persist = useCallback(
    async (conv: Conversation): Promise<Conversation> => {
      if (persistedIds.current.has(conv.id)) {
        return store.save(conv);
      }
      const created = await store.create(conv);
      persistedIds.current.add(conv.id);
      return created;
    },
    [store],
  );

  const newConversation = useCallback(() => {
    setActive(null);
    setDraftModelId((m) => m || DEFAULT_MODEL_ID);
  }, []);

  const select = useCallback(
    async (id: string) => {
      const seq = ++loadSeq.current;
      setLoadingActive(true);
      try {
        const convo = await store.get(id);
        if (seq !== loadSeq.current) return; // superseded
        if (convo && !convo.deleted) {
          persistedIds.current.add(convo.id);
          setActive(convo);
        }
      } finally {
        if (seq === loadSeq.current) setLoadingActive(false);
      }
    },
    [store],
  );

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || sending) return;

      // Ensure a conversation exists to append to (created in-memory).
      let convo = active;
      if (!convo) {
        const alias = await store.currentAlias();
        convo = blankConversation(alias, draftModelId);
        // Seed the new conversation with the current picker selection so its
        // grounded turns route to the chosen KB (and it persists per-convo).
        if (selectedKbKey) convo = { ...convo, kbBundleKey: selectedKbKey };
      }

      const isFirst = convo.messages.length === 0;
      const userMsg = makeMessage('user', trimmed);
      let working: Conversation = {
        ...convo,
        title: isFirst ? deriveTitle(trimmed) : convo.title,
        messages: [...convo.messages, userMsg],
        updatedAt: new Date().toISOString(),
      };
      setActive(working);
      setSending(true);

      // Persist the user turn (non-fatal if it fails; generation still runs).
      try {
        working = await persist(working);
        setActive(working);
      } catch {
        /* keep the in-memory turn; try to persist again after the reply */
      }

      // Generate the assistant reply: KB-grounded (RetrieveAndGenerate) against
      // the SELECTED knowledge base when grounding is on and a KB is selectable,
      // else the plain-LLM converse fallback (also the no-registered-KBs path).
      const kbKey = working.kbBundleKey ?? selectedKbKey ?? undefined;
      const useKb = grounded && !!kbKey;
      if (useKb) {
        try {
          const { answer, citations, sessionId } =
            await store.retrieveAndGenerate({
              query: trimmed,
              ...(working.kbSessionId
                ? { sessionId: working.kbSessionId }
                : {}),
              ...(kbKey ? { bundleKey: kbKey } : {}),
            });
          working = {
            ...working,
            ...(sessionId ? { kbSessionId: sessionId } : {}),
            messages: [
              ...working.messages,
              makeMessage(
                'assistant',
                answer.trim() || '_(empty response)_',
                false,
                { citations, grounded: true },
              ),
            ],
            updatedAt: new Date().toISOString(),
          };
        } catch (e) {
          const raw =
            e instanceof Error ? e.message : 'KB request failed.';
          working = {
            ...working,
            messages: [
              ...working.messages,
              makeMessage('assistant', friendlyKbError(raw), true),
            ],
            updatedAt: new Date().toISOString(),
          };
        }
      } else {
        try {
          const prompt = buildConversePrompt(working.messages);
          const outputText = await store.converse({
            modelId: working.modelId,
            prompt,
            systemPrompt: ARCHITECTURE_SYSTEM_PROMPT,
          });
          working = {
            ...working,
            messages: [
              ...working.messages,
              makeMessage(
                'assistant',
                outputText.trim() || '_(empty response)_',
                false,
                { grounded: false },
              ),
            ],
            updatedAt: new Date().toISOString(),
          };
        } catch (e) {
          const raw =
            e instanceof Error ? e.message : 'Inference request failed.';
          working = {
            ...working,
            messages: [
              ...working.messages,
              makeMessage('assistant', friendlyInferenceError(raw), true),
            ],
            updatedAt: new Date().toISOString(),
          };
        }
      }

      setActive(working);
      try {
        working = await persist(working);
        setActive(working);
      } catch {
        /* leave the in-memory transcript; list refresh below is best-effort */
      }
      setSending(false);
      void refresh();
    },
    [active, sending, draftModelId, store, persist, refresh, grounded, selectedKbKey],
  );

  const ingestFile = useCallback(
    async (fileName: string, content: string) => {
      if (ingesting) return;
      setIngesting(true);
      setIngestStatus(null);
      try {
        // RELATIVE object key — the backend prefixes it with the configured
        // bucket prefix and validates it. Just sanitize the file name.
        const objectKey = fileName.trim().replace(/^\/+/, '');
        const result = await store.uploadAndIngest(objectKey, content);
        setIngestStatus(
          `Uploaded ${result.objectKey} · ingestion ${result.ingestionStatus || 'started'}` +
            (result.ingestionJobId ? ` (job ${result.ingestionJobId})` : ''),
        );
      } catch (e) {
        const raw = e instanceof Error ? e.message : 'Upload/ingest failed.';
        setIngestStatus(`⚠️ ${raw}`);
        throw e;
      } finally {
        setIngesting(false);
      }
    },
    [ingesting, store],
  );

  const setModelId = useCallback(
    (modelId: string) => {
      setDraftModelId(modelId);
      setActive((prev) => {
        if (!prev) return prev;
        const updated: Conversation = { ...prev, modelId };
        // Persist the model change only if the conversation already exists.
        if (persistedIds.current.has(updated.id)) {
          void store.save(updated).then(() => void refresh());
        }
        return updated;
      });
    },
    [store, refresh],
  );

  const setKbKey = useCallback(
    (bundleKey: string) => {
      setDraftKbKey(bundleKey);
      setActive((prev) => {
        if (!prev) return prev;
        // applyKbSelection records the KB and resets kbSessionId so the next
        // grounded turn starts a fresh session against the new KB.
        const updated = applyKbSelection(prev, bundleKey);
        if (persistedIds.current.has(updated.id)) {
          void store.save(updated).then(() => void refresh());
        }
        return updated;
      });
    },
    [store, refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await store.softDelete(id);
      persistedIds.current.delete(id);
      setActive((prev) => (prev && prev.id === id ? null : prev));
      await refresh();
    },
    [store, refresh],
  );

  const modelId = active?.modelId ?? draftModelId;

  return {
    conversations,
    active,
    modelId,
    loading,
    loadingActive,
    sending,
    error,
    refresh,
    newConversation,
    select,
    send,
    setModelId,
    remove,
    grounded,
    setGrounded,
    tenantKbs,
    selectedKbKey,
    kbActive,
    setKbKey,
    ingesting,
    ingestStatus,
    ingestFile,
  };
}
